import type { LeaveRequest } from "@spork/s4p-server-sdk";
import { useColorScheme } from "nativewind";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, Switch, View } from "react-native";

import { toastError } from "@/components/AppToast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SheetModal } from "@/components/ui/sheet-modal";
import { Text } from "@/components/ui/text";
import { DateTimeRow } from "@/components/ui/date-time-row";
import { resolveLeaveRequestTimes } from "@/features/requests/leaveRequestTimes";
import {
    formatDisplayDate,
    parseIsoDate,
    serializeTimeMinutes,
} from "@/lib/date";
import { THEME, type ColorScheme } from "@/lib/theme";
import { useGetChildrenQuery, useGetSchoolYearQuery } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

type UpdateEndDateSheetProps = {
    visible: boolean;
    onClose: () => void;
    request: LeaveRequest;
    isBusy: boolean;
    isSaving: boolean;
    onSave: (dateTo: string, timeTo: string | null) => Promise<void>;
    formKey: string;
};

function laterDate(
    left: Date | undefined,
    right: Date | undefined,
): Date | undefined {
    if (!left) {
        return right;
    }

    if (!right) {
        return left;
    }

    return left > right ? left : right;
}

function isIsoDateWithinRange(
    date: string,
    firstDay: string,
    lastDay: string,
): boolean {
    return date >= firstDay && date <= lastDay;
}

export function UpdateEndDateSheet({
    visible,
    onClose,
    request,
    isBusy,
    isSaving,
    onSave,
    formKey,
}: UpdateEndDateSheetProps) {
    const { t } = useTranslation();
    const isOpenEnded = request.date_to == null || request.date_to === "";
    const title = isOpenEnded
        ? t("leave.setEndDate")
        : t("leave.changeEndDate");

    return (
        <SheetModal
            visible={visible}
            onRequestClose={onClose}
            dismissible={!isBusy}
            accessibilityLabel={t("leave.closeUpdateEndDateA11y")}
        >
            <View className="flex-row items-center justify-between border-b border-border px-4 py-3">
                <Text variant="label">{title}</Text>
                <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t("leave.closeUpdateEndDateA11y")}
                    onPress={onClose}
                    disabled={isBusy}
                    className="px-1 py-1 active:opacity-70"
                >
                    <Text className="text-primary">{t("leave.close")}</Text>
                </Pressable>
            </View>

            <UpdateEndDateForm
                key={formKey}
                request={request}
                isBusy={isBusy}
                isSaving={isSaving}
                onSave={onSave}
            />
        </SheetModal>
    );
}

type UpdateEndDateFormProps = {
    request: LeaveRequest;
    isBusy: boolean;
    isSaving: boolean;
    onSave: (dateTo: string, timeTo: string | null) => Promise<void>;
};

function UpdateEndDateForm({
    request,
    isBusy,
    isSaving,
    onSave,
}: UpdateEndDateFormProps) {
    const { t } = useTranslation();
    const { colorScheme } = useColorScheme();
    const scheme = (colorScheme ?? "light") as ColorScheme;
    const initialTimeTo = serializeTimeMinutes(request.time_to) ?? "";
    const [dateTo, setDateTo] = useState(request.date_to ?? "");
    const [isAllDay, setIsAllDay] = useState(initialTimeTo === "");
    const [timeTo, setTimeTo] = useState(initialTimeTo);
    const { data: children = [] } = useGetChildrenQuery();
    const child = children.find((item) => item.id === request.child_id);
    const userId =
        typeof child?.user_id === "number" ? child.user_id : undefined;
    const { data: schoolYear } = useGetSchoolYearQuery(userId ?? 0, {
        skip: userId == null,
    });

    const schoolYearMinimumDate = useMemo(
        () => (schoolYear ? parseIsoDate(schoolYear.first_day) : undefined),
        [schoolYear],
    );
    const schoolYearMaximumDate = useMemo(
        () => (schoolYear ? parseIsoDate(schoolYear.last_day) : undefined),
        [schoolYear],
    );
    const dateToMinimumDate = laterDate(
        parseIsoDate(request.date_from),
        schoolYearMinimumDate,
    );
    const dateToMaximumDate = schoolYearMaximumDate;

    async function handleSave() {
        const trimmedDateTo = dateTo.trim();

        if (!trimmedDateTo) {
            toastError(t("leave.errorTitle"), t("leave.endDateRequired"));
            return;
        }

        if (trimmedDateTo < request.date_from) {
            toastError(t("leave.errorTitle"), t("leave.endDateBeforeStart"));
            return;
        }

        if (
            schoolYear &&
            !isIsoDateWithinRange(
                trimmedDateTo,
                schoolYear.first_day,
                schoolYear.last_day,
            )
        ) {
            toastError(
                t("leave.errorTitle"),
                t("leave.dateOutsideSchoolYear", {
                    from: formatDisplayDate(schoolYear.first_day),
                    to: formatDisplayDate(schoolYear.last_day),
                }),
            );
            return;
        }

        if (!isAllDay && !timeTo.trim()) {
            toastError(t("leave.errorTitle"), t("leave.timeRequired"));
            return;
        }

        const resolvedTimes = resolveLeaveRequestTimes({
            isAllDay,
            timeFrom: isAllDay ? "" : (request.time_from ?? ""),
            timeTo: isAllDay ? "" : timeTo,
            dateFrom: request.date_from,
            dateTo: trimmedDateTo,
        });

        if (!resolvedTimes.ok) {
            toastError(t("leave.errorTitle"), t(`leave.${resolvedTimes.error}`));
            return;
        }

        await onSave(trimmedDateTo, resolvedTimes.timeTo);
    }

    return (
        <View className="gap-3 px-4 py-4">
            <View className="flex-row items-center justify-between gap-4">
                <Label>{t("leave.allDay")}</Label>
                <Switch
                    value={isAllDay}
                    onValueChange={(nextAllDay) => {
                        setIsAllDay(nextAllDay);
                        if (nextAllDay) {
                            setTimeTo("");
                        }
                    }}
                    trackColor={{
                        false: THEME[scheme].border,
                        true: THEME[scheme].primary,
                    }}
                    thumbColor="#FFFFFF"
                    testID={
                        TEST_IDS[
                            TEST_IDS.REQUESTS_LIST_ITEM_UPDATE_END_DATE_ALL_DAY_SWITCH
                        ]
                    }
                    accessibilityLabel={t("leave.allDay")}
                />
            </View>
            <DateTimeRow
                label={t("leave.end")}
                dateValue={dateTo}
                onDateChange={setDateTo}
                dateTestID={
                    TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM_UPDATE_END_DATE_FIELD]
                }
                minimumDate={dateToMinimumDate}
                maximumDate={dateToMaximumDate}
                time={
                    isAllDay
                        ? undefined
                        : {
                              value: timeTo,
                              onChange: setTimeTo,
                              testID: TEST_IDS[
                                  TEST_IDS
                                      .REQUESTS_LIST_ITEM_UPDATE_END_DATE_TIME_TO_FIELD
                              ],
                          }
                }
            />
            <Button
                disabled={isBusy}
                onPress={() => {
                    void handleSave();
                }}
                testID={
                    TEST_IDS[
                        TEST_IDS.REQUESTS_LIST_ITEM_UPDATE_END_DATE_SUBMIT_BUTTON
                    ]
                }
            >
                <Text className="text-primary-foreground">
                    {isSaving
                        ? t("leave.savingEndDate")
                        : t("leave.saveEndDate")}
                </Text>
            </Button>
        </View>
    );
}
